import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Company, Deal, Person, Route } from "@mapmycustomers/shared/types/entity";
import { PlatformFilterCondition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import { RootState } from "@app/store/rootReducer";
import { personLayoutModel } from "@app/util/layout/impl";

import Relationships from "../../components/Relationships";
import { PEOPLE_RELATIONSHIP_TYPES } from "../../enum/RelationshipType";
import {
  addPersonCompanies,
  addPersonDeals,
  fetchMoreCompanies,
  fetchMoreDeals,
  fetchPersonRoutes,
  removePersonCompany,
  removePersonDeal,
  removePersonFromRoute,
  updatePersonPrimaryCompany,
} from "../store/actions";
import {
  getRemovePersonDealLoadingIds,
  getRemovePersonParentCompanyLoadingIds,
  getRemovePersonRouteLoadingsIds,
  isAddPersonDealsLoading,
  isAddPersonParentCompanyLoading,
  isPersonRoutesLoading,
} from "../store/selectors";

interface Props {
  companies: Company[];
  deals: Deal[];
  person: Person;
  routes: Route[];
  totalCompanies: number;
  totalDeals: number;
}

const RelationshipsContainer: React.FC<
  Props & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>
> = ({
  addPersonCompanies,
  addPersonDeals,
  addPersonDealsLoading,
  addPersonParentCompanyLoading,
  companies,
  deals,
  fetchMoreCompanies,
  fetchMoreDeals,
  fetchPersonRoutes,
  person,
  personRoutesLoading,
  removePersonCompany,
  removePersonDeal,
  removePersonFromRoute,
  removePersonRouteLoadingIds,
  routes,
  totalCompanies,
  totalDeals,
  updatePersonPrimaryCompany,
}) => {
  const intl = useIntl();

  const [modalVisible, onShow, onHide] = useBoolean();

  const [primaryCompanyId, setPrimaryCompanyId] = useState<Company["id"] | undefined>(
    person.primaryAccountId ?? undefined
  );

  const entityLayout = useMemo(() => personLayoutModel.getLayoutFor(person), [person]);

  const requestFilters: PlatformFilterCondition[] = useMemo(
    () => [
      {
        $and: [
          {
            id: { $in: person.accounts.map(({ id }) => id) },
          },
        ],
      },
    ],
    [person.accounts]
  );

  const handleAddToRoute = useCallback(
    () => fetchPersonRoutes(person.id),
    [person.id, fetchPersonRoutes]
  );

  const handleRemoveParentCompany = useCallback(
    (company: Company) =>
      removePersonCompany({ id: person.id, companyId: company.id, layoutId: entityLayout.id }),
    [entityLayout.id, person.id, removePersonCompany]
  );

  const handleSelectPrimaryCompany = useCallback(
    (primaryCompanyIds: Company["id"][]) => {
      if (primaryCompanyIds.length) {
        updatePersonPrimaryCompany({
          id: person.id,
          accounts: person.accounts,
          primaryCompanyId: primaryCompanyIds[0],
        });
      }
    },
    [person.accounts, person.id, updatePersonPrimaryCompany]
  );

  const handleUpdatePrimaryCompany = useCallback(
    (primaryCompanyId?: Company["id"]) => setPrimaryCompanyId(primaryCompanyId),
    []
  );

  const handleUnsetPrimaryCompany = useCallback(() => {
    updatePersonPrimaryCompany({
      id: person.id,
      accounts: person.accounts,
      primaryCompanyId: null,
    });
  }, [person.accounts, person.id, updatePersonPrimaryCompany]);

  const getListTitle = useCallback(
    (count: number, total: number) =>
      intl.formatMessage(
        {
          id: "preview.personRecordPane.relationshipsContainer.primaryCompanyModal.subtitle",
          defaultMessage: "Associated Companies ({count}/{total})",
          description: "Subtitle of a Select Primary Company modal in the Preview Record pane",
        },
        { count, total }
      ),
    [intl]
  );

  useEffect(() => {
    setPrimaryCompanyId(person.primaryAccountId ?? undefined);
  }, [person.primaryAccountId]);

  return (
    <>
      <Relationships
        addDealsLoading={addPersonDealsLoading}
        addParentCompanyLoading={addPersonParentCompanyLoading}
        addRoutesLoading={personRoutesLoading}
        companies={companies}
        deals={deals}
        entity={person}
        onAddToRoute={handleAddToRoute}
        onChangePrimaryCompany={onShow}
        onFetchMoreCompanies={fetchMoreCompanies}
        onFetchMoreDeals={fetchMoreDeals}
        onRemoveDeal={removePersonDeal}
        onRemoveParentCompany={handleRemoveParentCompany}
        onRemoveRoute={removePersonFromRoute}
        onSelectDeals={addPersonDeals}
        onSelectParentCompanies={addPersonCompanies}
        onUnsetPrimaryCompany={person.primaryAccount ? handleUnsetPrimaryCompany : undefined}
        onUpdatePrimaryCompany={handleUpdatePrimaryCompany}
        primaryCompanyId={primaryCompanyId}
        removeRouteLoadingIds={removePersonRouteLoadingIds}
        routes={routes}
        supportedRelationshipTypes={PEOPLE_RELATIONSHIP_TYPES}
        totalCompanies={totalCompanies}
        totalDeals={totalDeals}
      />
      {modalVisible && (
        <CompanyAssociation
          allowAdd={false}
          assignedCompanies={person.primaryAccount ? [person.primaryAccount] : undefined}
          associateWith={person.id}
          entityType={person.entity}
          getListTitle={getListTitle}
          multiselect={false}
          onHide={onHide}
          onSelect={handleSelectPrimaryCompany}
          requestFilters={requestFilters}
          title={intl.formatMessage(
            {
              id: "preview.personRecordPane.relationshipsContainer.primaryCompanyModal.title",
              defaultMessage: "Set Primary Company for {personName}",
              description: "Title of a Select Primary Company modal in the Preview Record pane",
            },
            { personName: person.name }
          )}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  addPersonDealsLoading: isAddPersonDealsLoading(state),
  addPersonParentCompanyLoading: isAddPersonParentCompanyLoading(state),
  personRoutesLoading: isPersonRoutesLoading(state),
  removePersonDealLoadingIds: getRemovePersonDealLoadingIds(state),
  removePersonParentCompanyLoadingIds: getRemovePersonParentCompanyLoadingIds(state),
  removePersonRouteLoadingIds: getRemovePersonRouteLoadingsIds(state),
});

const mapDispatchToProps = {
  addPersonCompanies: addPersonCompanies.request,
  addPersonDeals: addPersonDeals.request,
  fetchMoreCompanies: fetchMoreCompanies.request,
  fetchMoreDeals: fetchMoreDeals.request,
  fetchPersonRoutes: fetchPersonRoutes.request,
  removePersonCompany: removePersonCompany.request,
  removePersonDeal: removePersonDeal.request,
  removePersonFromRoute: removePersonFromRoute.request,
  updatePersonPrimaryCompany: updatePersonPrimaryCompany.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(RelationshipsContainer);
